import { Component } from '@theme/component';

/**
 * @typedef {object} ApcFlowboxInspirationRefs
 * @property {HTMLElement} placeholder - Skeleton grid shown until the Flowbox feed renders.
 * @property {HTMLElement} flow - The Flowbox feed container (also the SDK's `init` target).
 */

/**
 * Two-level tag filtering on top of an embedded Flowbox flow.
 *
 * The flow is booted inline in the section Liquid (queue + `init`); this component owns the
 * interactivity: clicking a category reveals its tag sub-row and refilters the flow, clicking a tag
 * filters further. Each filter change re-shows the skeleton placeholder, which is hidden again once
 * Flowbox injects its feed grid (watched via MutationObserver, with a timeout fallback).
 *
 * Faithful port of pink-gellac's `flowbox-inspiration` (keen-slider/choozy) onto the APA component
 * system — filter rows are native horizontal-scroll instead of a carousel.
 *
 * @extends {Component<ApcFlowboxInspirationRefs>}
 */
class ApcFlowboxInspiration extends Component {
  requiredRefs = ['placeholder', 'flow'];

  /** @type {string} */
  #key = '';
  /** @type {string} */
  #locale = 'en-NL';
  /** @type {string} */
  #container = '';
  /** @type {string[]} */
  #allTags = [];

  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #placeholderTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#key = this.dataset.flowboxId ?? '';
    this.#locale = this.dataset.locale || 'en-NL';
    this.#container = `#${this.refs.flow.id}`;
    this.#allTags = (this.dataset.allTags ?? '').split(',').filter(Boolean);

    // The flow may already be rendering from the inline `init`, so start watching immediately.
    this.#watchForFeed();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    clearTimeout(this.#placeholderTimeout);
  }

  /* -------------------------------------------------------------------------
   * Declarative click handlers
   * ---------------------------------------------------------------------- */

  /**
   * Selects a category: highlights it, reveals its tag sub-row (if any) and refilters the flow with
   * the category's tags — or the full tag set for the tag-less "All" category.
   *
   * @param {Event} event
   */
  selectCategory = (event) => {
    const button = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('[data-category-button]')
    );
    if (!button) return;

    this.#setActive(this.#categoryButtons, button);
    this.#revealTagRow(button.dataset.category ?? '');

    const tags = (button.dataset.categoryTags ?? '').split(',').filter(Boolean);
    this.#updateFlow(tags.length ? tags : this.#allTags, 'any');
  };

  /**
   * Selects a single tag within the active sub-row and filters the flow to it.
   *
   * @param {Event} event
   */
  selectTag = (event) => {
    const button = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('[data-tag]'));
    if (!button) return;

    const row = button.closest('[data-category-block]');
    if (row) this.#setActive(row.querySelectorAll('[data-tag]'), button);

    const tag = button.dataset.tag ?? '';
    this.#updateFlow([tag], 'all');
  };

  /* -------------------------------------------------------------------------
   * Internals
   * ---------------------------------------------------------------------- */

  /** @returns {NodeListOf<HTMLElement>} */
  get #categoryButtons() {
    return this.querySelectorAll('[data-category-button]');
  }

  /**
   * Toggles the filled/outline button styles and `aria-pressed` across a set of filter buttons.
   *
   * @param {Iterable<Element>} buttons
   * @param {Element} active
   */
  #setActive(buttons, active) {
    for (const button of buttons) {
      const isActive = button === active;
      button.classList.toggle('button-primary', isActive);
      button.classList.toggle('button-secondary', !isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }
  }

  /**
   * Shows the tag sub-row for the given category (resetting its tag buttons) and hides the rest.
   *
   * @param {string} categoryId
   */
  #revealTagRow(categoryId) {
    for (const row of this.querySelectorAll('[data-category-block]')) {
      const isMatch = row instanceof HTMLElement && row.dataset.category === categoryId;
      /** @type {HTMLElement} */ (row).hidden = !isMatch;

      if (isMatch) {
        for (const tag of row.querySelectorAll('[data-tag]')) {
          tag.classList.remove('button-primary');
          tag.classList.add('button-secondary');
          tag.setAttribute('aria-pressed', 'false');
        }
      }
    }
  }

  /**
   * Re-filters the embedded flow and re-shows the placeholder until the new feed renders.
   *
   * @param {string[]} tags
   * @param {'any' | 'all'} tagsOperator
   */
  #updateFlow(tags, tagsOperator) {
    this.#showPlaceholder();

    if (window.flowbox) {
      window.flowbox('update', {
        container: this.#container,
        key: this.#key,
        tags,
        tagsOperator,
      });
    }

    this.#watchForFeed();
  }

  /** Re-arms the feed observer and the timeout fallback. */
  #watchForFeed() {
    // Fast path: the feed may already be present (e.g. the inline init rendered before this element
    // upgraded), in which case no mutation fires — hide the skeleton now instead of waiting it out.
    if (this.refs.flow.querySelector('.bzfy-t-feed-tile-grid, .bzfy-t-feed-carousel')) {
      this.#hidePlaceholder();
      return;
    }

    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      if (this.refs.flow.querySelector('.bzfy-t-feed-tile-grid, .bzfy-t-feed-carousel')) {
        this.#hidePlaceholder();
        this.#observer?.disconnect();
      }
    });
    this.#observer.observe(this.refs.flow, { childList: true, subtree: true });

    // Safety net: never leave the skeleton stuck if Flowbox updates in place without re-injecting.
    clearTimeout(this.#placeholderTimeout);
    this.#placeholderTimeout = setTimeout(() => this.#hidePlaceholder(), 4000);
  }

  #showPlaceholder() {
    this.refs.placeholder.hidden = false;
  }

  #hidePlaceholder() {
    this.refs.placeholder.hidden = true;
  }
}

if (!customElements.get('apc-flowbox-inspiration')) {
  customElements.define('apc-flowbox-inspiration', ApcFlowboxInspiration);
}
